import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryClient } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { dedupeMeetingsByEvent } from './meeting-row-state';
import { foldMeetingPages } from './meeting-history-pages';
import type { UpcomingMeetingState } from './meeting-row-state';
import { hasMeetingsCalendar, MEETINGS_SETUP_KEY, meetingsConnectionPresent, type MeetingsSetup } from './meetings-chats-empty';

export {
  dedupeMeetingsByEvent,
  groupByDay,
  meetingRowAction,
  meetingRowDecision,
  meetingRowKey,
  meetingsNotEnded,
  MEETING_STATE_BADGE,
  splitNextMeeting,
} from './meeting-row-state';
export type { MeetingRowAction, UpcomingMeetingState } from './meeting-row-state';

/**
 * The Meetings channel's client contract — the shapes the three `/api/meetings/*` proxies answer with,
 * and the reads and writes the card is built on.
 *
 * The wire strings are spelled out here rather than imported from the common-lib enum on purpose: the
 * enum is a VALUE from that barrel, and in a worktree the barrel resolves to the main checkout's copy
 * of the lib — so importing it for its value makes this module, and anything testing it, unrunnable
 * outside CI. Same reasoning the harness route gives for its own copy of these two strings.
 */
export type MeetingJoinPolicy = 'ALL_VIDEO_EVENTS' | 'REQUESTED_ONLY';

export interface MeetingConnection {
  joinPolicy: MeetingJoinPolicy;
  /** Whether she may answer out loud when addressed. She always records and takes notes regardless. */
  canTalk: boolean;
  connectedAt?: string;
}

export interface UpcomingMeeting {
  calendarEventId: string;
  calendarTokenId: string;
  calendarId: string;
  title?: string;
  startsAt: string;
  endsAt?: string;
  meetingUrl: string;
  participantCount: number;
  /** The first few attendees, for the row's avatar stack. Absent from an older server. */
  participantNames?: string[];
  state: UpcomingMeetingState;
  meetingSessionId?: string;
}

export interface MeetingsSchedule {
  /**
   * Whether the server would let the clock join for this site at all — the product kill switch and its
   * allowlist. A connected site can still have this false, and the card has to say so: "you switched
   * her on" and "we have switched this on for you" are different facts, and only one of them is the
   * user's to change.
   *
   * **Renamed off the wire deliberately.** Upstream calls this `autoJoinEnabled`, one word away from
   * the `joinPolicy === 'ALL_VIDEO_EVENTS'` switch the card labels "join automatically" — and the two
   * are unrelated facts: this one is ours and the user cannot move it, that one is theirs and we never
   * do. Two near-identical names for those two things in one component is a mis-read waiting to
   * happen, so the wire name stops at `readSchedule`.
   */
  joiningAllowedForSite: boolean;
  connection: MeetingConnection | null;
  calendars: { calendarTokenId: string; email?: string }[];
  meetings: UpcomingMeeting[];
}

/** The body `/api/meetings/upcoming` answers with, before `readSchedule` renames its switch. */
interface MeetingsScheduleWire extends Omit<MeetingsSchedule, 'joiningAllowedForSite'> {
  autoJoinEnabled: boolean;
}

export const meetingsKeys = {
  schedule: ['meetings', 'schedule'] as const,
  history: ['meetings', 'history'] as const,
  /** The one-page lookup the chat header falls back to — see `lookupMeetingByConversation`. */
  historyLookup: ['meetings', 'history', 'lookup'] as const,
  setup: MEETINGS_SETUP_KEY,
};

export class MeetingsActionError extends Error {
  constructor(public status: number) {
    super(`HTTP ${status}`);
  }
}

const SCHEDULE_URL = `${API_BASE}/api/meetings/upcoming`;
const CONNECTION_URL = `${API_BASE}/api/meetings/connection`;
const HISTORY_URL = `${API_BASE}/api/meetings/history`;
const REQUEST_URL = `${API_BASE}/api/meetings/request`;
const EVENT_THREAD_URL = `${API_BASE}/api/meetings/event-thread`;
const CALENDARS_URL = `${API_BASE}/api/calendars`;

/**
 * An older harness answers `connection` with no `canTalk` at all — that account never had the field,
 * not an account that opted in — so a missing value reads as off, the same default a first connect gets.
 */
function normalizeConnection(connection: MeetingConnection | null | undefined): MeetingConnection | null {
  if (!connection) return null;
  return { ...connection, canTalk: connection.canTalk === true };
}

async function readSchedule(): Promise<MeetingsSchedule> {
  const res = await apiFetch(SCHEDULE_URL, { credentials: 'include' });
  if (!res.ok) throw new MeetingsActionError(res.status);
  const json = (await res.json()) as Partial<MeetingsScheduleWire>;
  return {
    joiningAllowedForSite: json.autoJoinEnabled === true,
    connection: normalizeConnection(json.connection),
    calendars: json.calendars ?? [],
    // One row per meeting, not per calendar copy of it — the decision behind a row is stored per
    // (site, event) upstream, so two copies of one event are one decision rendered twice.
    meetings: dedupeMeetingsByEvent(json.meetings ?? []),
  };
}

/**
 * One read behind the whole card: the connection, the calendars it covers, the server switch and the
 * week's meetings all arrive together, so the card can never render a connected state against a stale
 * list or the other way round.
 *
 * Deliberately not refetched on window focus. The upstream re-arms capsules on its way out, so this
 * GET legitimately writes — a background refetch every time a tab regains focus would be arming work
 * nobody asked for.
 */
export function useMeetingsSchedule() {
  const query = useQuery({
    queryKey: meetingsKeys.schedule,
    queryFn: readSchedule,
    refetchOnWindowFocus: false,
  });
  return { schedule: query.data, loading: query.isLoading, error: query.isError, refetch: query.refetch };
}

/**
 * One meeting's live row, off the same read the Meetings list renders — for the join chip in that
 * meeting's conversation (`lib/meeting-join-chip.ts`). Undefined while loading, or for an event the
 * week's list does not hold. Fresh for a minute, so opening several meeting threads in a row does not
 * re-run the read's arming pass for each.
 */
export function useUpcomingMeeting(calendarEventId: string) {
  const query = useQuery({
    queryKey: meetingsKeys.schedule,
    queryFn: readSchedule,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
  return { meeting: query.data?.meetings.find((meeting) => meeting.calendarEventId === calendarEventId) };
}

async function readConnected(url: string, present: (json: Record<string, unknown>) => boolean): Promise<boolean | null> {
  try {
    const res = await apiFetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    return present((await res.json()) as Record<string, unknown>);
  } catch {
    return null;
  }
}

/**
 * The two facts the Chats Calendar Meetings tab needs for its empty state: is a calendar connected, and
 * is the Meetings channel. Deliberately NOT `useMeetingsSchedule` — that GET reads a week of events and
 * re-arms capsules on its way out, and an empty list is no reason to do either. `/api/calendars` is the
 * calendar-token read Settings and the connectors page use. A failed half is `null`, which `meetingsChatsEmpty` turns into no CTA. Mounting the
 * empty state is the gate, so a list with rows spends no request on it.
 */
export function useMeetingsSetup(): MeetingsSetup | undefined {
  const query = useQuery({
    queryKey: meetingsKeys.setup,
    queryFn: async (): Promise<MeetingsSetup> => {
      const [calendarConnected, meetingsConnected] = await Promise.all([
        readConnected(CALENDARS_URL, hasMeetingsCalendar),
        readConnected(CONNECTION_URL, meetingsConnectionPresent),
      ]);
      return { calendarConnected, meetingsConnected };
    },
    refetchOnWindowFocus: false,
  });
  return query.data;
}

async function connectionRequest(method: string, body?: unknown): Promise<void> {
  const res = await apiFetch(CONNECTION_URL, {
    method,
    credentials: 'include',
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!res.ok) throw new MeetingsActionError(res.status);
}

/**
 * Connect, the two switches, and disconnect.
 *
 * Every one of them invalidates the schedule rather than patching it: flipping automatic joining
 * changes the state of every row in the list at once, and a card that showed the new switch position
 * over the old row states would be showing a promise it had already broken.
 *
 * A first connect asks for `REQUESTED_ONLY` explicitly. Pressing Connect is consent to the feature,
 * not consent to her walking into every interview and 1:1 on the calendar — the user turns automatic
 * on with the switch, which is a second, separate decision.
 *
 * **`useMutation` rather than the house's imperative cache edits (CLAUDE.md, Data fetching), on
 * purpose.** That style exists so a write can be shown optimistically and rolled back, and it needs
 * the new value to be a local edit of the old one. None of these four are: flipping automatic joining
 * re-decides every row in the list server-side, and the row states are the answer, not the switch. An
 * optimistic patch here would be a promise the page has not been told it can keep, so all four
 * invalidate and wait, and the `isLoading` each one exposes is what disables its control meanwhile.
 */
export function useMeetingsChannelActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: meetingsKeys.schedule });
  const invalidateConnection = () => Promise.all([invalidate(), qc.invalidateQueries({ queryKey: meetingsKeys.setup })]);

  // Settled, not success: a connect can follow a calendar connect in the same press, and when the
  // connect then fails the card must still re-read, or it keeps showing "no calendar" for one it has.
  const connect = useMutation({
    mutationFn: () => connectionRequest('POST', { joinPolicy: 'REQUESTED_ONLY' satisfies MeetingJoinPolicy }),
    onSettled: () => invalidateConnection(),
  });
  const update = useMutation({
    mutationFn: (patch: { joinPolicy?: MeetingJoinPolicy; canTalk?: boolean }) => connectionRequest('POST', patch),
    onSuccess: invalidate,
  });
  const disconnect = useMutation({
    mutationFn: () => connectionRequest('DELETE'),
    onSuccess: invalidateConnection,
  });
  const decide = useMutation({
    mutationFn: async (input: { meeting: UpcomingMeeting; decision: 'join' | 'skip' | null }) => {
      const res = await apiFetch(REQUEST_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          calendarEventId: input.meeting.calendarEventId,
          calendarTokenId: input.meeting.calendarTokenId,
          calendarId: input.meeting.calendarId,
          decision: input.decision,
        }),
      });
      if (!res.ok) throw new MeetingsActionError(res.status);
    },
    onSuccess: invalidate,
  });

  // A calendar reconnected under an existing connection changes the calendars and the week, not the
  // connection — so it re-reads rather than posting a connect.
  const refresh = () => void invalidate();

  return { connect, update, disconnect, decide, refresh };
}

/**
 * The conversation an upcoming meeting opens into. Created on the first open and the same one after, and
 * the meeting's own thread from then on: the join adopts it, so the room's questions and the write-up land
 * in it too.
 *
 * `clientCapabilities: ['link-chips']` is the handshake for the opening row's "Join this meeting" chip
 * (`ChoiceOption.url`/`expiresAt` in `lib/message-choices.ts`): the server only writes that chip once the
 * client has declared it can render a LINK option rather than sending its label as an ordinary answer.
 */
export async function openMeetingEventThread(meeting: Pick<UpcomingMeeting, 'calendarEventId' | 'calendarTokenId' | 'calendarId'>): Promise<string> {
  const res = await apiFetch(EVENT_THREAD_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      calendarEventId: meeting.calendarEventId,
      calendarTokenId: meeting.calendarTokenId,
      calendarId: meeting.calendarId,
      clientCapabilities: ['link-chips'],
    }),
  });
  if (!res.ok) throw new MeetingsActionError(res.status);
  const json = (await res.json()) as { conversationId?: unknown };
  if (typeof json.conversationId !== 'string' || !json.conversationId) throw new MeetingsActionError(502);
  return json.conversationId;
}

/**
 * One note a meeting produced, still in fields.
 *
 * The harness renders these into the write-up's markdown, and `renderMeetingSummary` flattens an
 * action item's owner and due date into a parenthetical sentence on the way — so the conversation
 * cannot be read back into structure. The history row keeps them, and this is that shape.
 */
export interface PastMeetingNote {
  kind: 'note' | 'action_item';
  text: string;
  owner?: string;
  due?: string;
}

/**
 * A meeting she has already been in. `recordedAt` is when the WRITE-UP landed, in epoch ms;
 * `startedAt` / `endedAt` are the MEETING's own span, from the transcript's first and last stamps. The
 * row shows the meeting's time and falls back to `recordedAt` only for a meeting recorded before the
 * span existed, or one in which nobody spoke.
 */
export interface PastMeeting {
  meetingSessionId: string;
  conversationId: string;
  title?: string;
  recordedAt: number;
  startedAt?: number;
  endedAt?: number;
  meetingUrl?: string;
  /** Display names from the room — text, never links. Absent on rows written before the field existed. */
  participants?: { name: string; isHost: boolean }[];
  speakers?: { name: string; isAgent: boolean }[];
  notes: PastMeetingNote[];
  /** Her session ended before anything was written up: the row says "No notes" and reads no file. */
  noNotes?: boolean;
  notesPath?: string;
  transcriptPath?: string;
}

/**
 * The history row names its files in the AGENT's form; the client's file readers take the STORED form
 * the chat attachments carry. A meeting's files are `transcripts/calendar-meetings/…`, one spelling in
 * both. A row written before they moved still names `spaces/meetings/…`, whose stored form is
 * `workspaces/…` — the harness's `toStoredVfsPath`.
 */
export function meetingFileVfsPath(agentPath: string | undefined): string | undefined {
  if (!agentPath) return undefined;
  return agentPath.startsWith('spaces/') ? `workspaces/${agentPath.slice('spaces/'.length)}` : agentPath;
}

export interface MeetingHistory {
  meetings: PastMeeting[];
  /** Pass back as `before` for the next page. Absent means this was the last one. */
  cursor?: number;
}

/**
 * How many meetings a page asks for: the latest five, then "Show earlier meetings" for five more. This
 * is a section on someone's agent page, and a long list of it is what the number is here to prevent.
 */
export const MEETING_HISTORY_PAGE = 5;

async function readMeetingHistory(before?: number): Promise<MeetingHistory> {
  const search = new URLSearchParams({ limit: String(MEETING_HISTORY_PAGE) });
  if (before !== undefined) search.set('before', String(before));
  const res = await apiFetch(`${HISTORY_URL}?${search}`, { credentials: 'include' });
  // A site outside the release answers 501, which is not an error worth a red line in the Meetings
  // view: no meeting of theirs was ever joined, so there is nothing to list.
  if (!res.ok) {
    if (res.status === 501) return { meetings: [] };
    throw new MeetingsActionError(res.status);
  }
  return (await res.json()) as MeetingHistory;
}

/**
 * The upstream's own page cap — the widest one read can look.
 */
const MEETING_HISTORY_LOOKUP_LIMIT = 50;

/**
 * Which meeting a conversation is the write-up of, from the history list rather than from the rows.
 *
 * The header normally learns this from the marker on the write-up row, but the chat loads a bounded
 * window (40 rows) and the marker sits on the FIRST row of the thread — so a meeting whose thread has
 * grown past the window shows no marker at all. This is the independent source: one read of the most
 * recent fifty meetings, cached, answering with the same facts the marker carries. A meeting older than
 * those fifty is not found; the header then draws its ordinary title, which is the honest degradation.
 *
 * Read through `queryClient.fetchQuery` so every long thread shares ONE request per five minutes.
 */
export async function lookupMeetingByConversation(conversationId: string): Promise<PastMeeting | null> {
  try {
    const page = await queryClient.fetchQuery({
      queryKey: meetingsKeys.historyLookup,
      queryFn: async () => {
        const search = new URLSearchParams({ limit: String(MEETING_HISTORY_LOOKUP_LIMIT) });
        const res = await apiFetch(`${HISTORY_URL}?${search}`, { credentials: 'include' });
        // 501 is the site being outside the release: there are no meetings, and caching that is right.
        if (res.status === 501) return { meetings: [] } as MeetingHistory;
        // Anything else — a 5xx, an auth answer while the session settles — must THROW rather than
        // resolve empty, or React Query caches "this account has no meetings" for five minutes and every
        // long thread opened in that window loses its header even after the service recovers.
        if (!res.ok) throw new MeetingsActionError(res.status);
        return (await res.json()) as MeetingHistory;
      },
      staleTime: 5 * 60_000,
    });
    return page.meetings.find((meeting) => meeting.conversationId === conversationId) ?? null;
  } catch {
    return null;
  }
}

/**
 * The site's past meetings, a page at a time.
 *
 * The section that calls this mounts only in the connected Meetings view, so mounting IS the gate and a
 * site outside the release, or one that never connected, spends no request.
 *
 * Paged on the store's own cursor — keyset on `recordedAt`, so a meeting written up while someone is
 * reading cannot shift a page boundary and make a row appear twice or not at all.
 *
 * **The cursor is stepped by one, and the rows are deduped — the two go together.** The cursor is a
 * TIMESTAMP and the store filters `lessThan` it, so two meetings recorded in the same millisecond can
 * straddle a boundary and the second is stepped straight over, unreachable at any page size. Asking
 * for `cursor + 1` asks for `<= cursor` instead, which hands the boundary row back a second time; the
 * `seen` set below is what makes that free. Dropping either half reintroduces a bug: without the step
 * a tied meeting is lost, without the set it is shown twice.
 */
export function useMeetingHistory() {
  const query = useInfiniteQuery({
    queryKey: meetingsKeys.history,
    queryFn: ({ pageParam }: { pageParam?: number }) => readMeetingHistory(pageParam),
    getNextPageParam: (last: MeetingHistory) => (last.cursor === undefined ? undefined : last.cursor + 1),
    refetchOnWindowFocus: false,
  });

  const { meetings, stalled } = foldMeetingPages(query.data?.pages ?? []);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  return {
    meetings,
    isLoading: query.isLoading,
    /**
     * A read failed — the first one, or a page. Deliberately NOT narrowed to the first: a failed
     * `fetchNextPage` leaves `data.pages` in place, so the caller can keep the rows it already has
     * and still report the failure. Which of the two it was is `meetings.length`.
     */
    isError: query.isError,
    /**
     * Whether pressing again can actually produce something. The server stops sending a cursor at the
     * end of the history; `stalled` covers the other ending, where it keeps sending one but every row
     * behind it has already been shown (see `foldMeetingPages`).
     */
    hasMore: Boolean(hasNextPage) && !stalled,
    isLoadingMore: isFetchingNextPage,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
  };
}
